import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react'
import useSWR from 'swr'
import { formStepsReducer, formStepsInitialState } from '@/reducer'
import {
  fetchBranchManager,
  searchAllBranchDealers,
  searchAllDealers,
} from '@/api/maintenance-tickets-api'
import { DealersData } from '@/utilities/types'

interface FormData {
  selectedDealer: string | null
  selectedBranchDealer: DealersData | null
  selectedConcern: string | null
  selectedCollateral: string | null
  selectedAppliance: string | null
  typeOfConcern: string | null
  typeOfConcernValue: string | null
  typeOfConcernOthers: string | null
  costOfSharing: string | null
  selectedRepairMethod: string | null
  costAmount: string
  description: string
  concernAttachments: File[]
  quotationAttachments: File[]
  branchId: number | null
  purpose: string | null
}

interface FormStepsContextType {
  dealers: string[] | undefined
  dealersLoading: boolean
  branchDealers: DealersData[] | undefined
  formData: FormData
  setFormData: React.Dispatch<React.SetStateAction<FormData>>
  role: string
  branchManager: any
}

const FormStepsContext = createContext<FormStepsContextType | undefined>(
  undefined,
)

export const useFormStepsContext = (): FormStepsContextType => {
  const context = useContext(FormStepsContext)
  if (!context) {
    throw new Error(
      'useFormStepsContext must be used within a FormStepsContextProvider',
    )
  }
  return context
}

export const FormStepsContextProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [dealersLoading, setDealersLoading] = useState(false)

  const role = JSON.parse(localStorage.getItem('role') || '')
  const userId = JSON.parse(localStorage.getItem('userId') || '0')

  const [formData, setFormData] = useState<FormData>({
    selectedDealer: null,
    selectedBranchDealer: null,
    selectedConcern: null,
    selectedCollateral: null,
    selectedAppliance: null,
    typeOfConcern: null,
    typeOfConcernValue: null,
    typeOfConcernOthers: null,
    selectedRepairMethod: null,
    costOfSharing: null,
    costAmount: '',
    description: '',
    purpose: null,
    concernAttachments: [],
    quotationAttachments: [],
    branchId: null,
  })

  const getAllDealers = async () => {
    setDealersLoading(true)
    try {
      const response = await searchAllDealers()
      console.log('dealer response ', response.data.data)
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch dealers:', error)
      throw error
    } finally {
      setDealersLoading(false)
    }
  }

  const getAllBranchDealers = async () => {
    try {
      const response = await searchAllBranchDealers(
        formData?.selectedDealer ?? '',
      )
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch branch dealers:', error)
      throw error
    }
  }

  const getBranchManager = async () => {
    try {
      const response = await fetchBranchManager({
        sourceUserId: parseInt(userId),
      })
      return response.data.data ?? {}
    } catch (error) {
      console.error('Failed to fetch branch dealers:', error)
      throw error
    }
  }

  const { data: dealers } = useSWR(`/v2/branch/dealers`, getAllDealers, {
    revalidateOnFocus: false,
  })

  const { data: branchDealers } = useSWR(
    formData.selectedDealer
      ? `/branch/?dealerName=${formData.selectedDealer}`
      : null,
    getAllBranchDealers,
    { revalidateOnFocus: false },
  )

  const { data: branchManager } = useSWR(
    role === 'Branch Manager' || role === 'OIC'
      ? `/v1/branch/get/dealer/by/branch-manager`
      : null,
    getBranchManager,
    { revalidateOnFocus: false },
  )

  const [formStepsState] = useReducer(formStepsReducer, formStepsInitialState)

  const stateRef = useRef(formStepsState)
  useEffect(() => {
    stateRef.current = formStepsState
  }, [formStepsState])

  const value = useMemo(
    () => ({
      dealers,
      dealersLoading,
      branchDealers,
      formData,
      setFormData,
      role,
      branchManager,
    }),
    [dealers, dealersLoading, branchDealers, formData, role, branchManager],
  )

  return (
    <FormStepsContext.Provider value={value}>
      {children}
    </FormStepsContext.Provider>
  )
}
